import { Container, Grid, Typography } from '@material-ui/core'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import Image from 'next/legacy/image'
import React, { PropsWithChildren } from 'react'
import styled from 'styled-components'
import { theme } from '../../theme'
import { irem } from '../../utils'

export type CTAWrapperProps = React.ComponentProps<typeof StyledContainer> & {
    imageURL: string
    imageAlt?: string 
}

export type CTAContentProps = React.ComponentProps<typeof ContentContainer> & {
    //
  }

export type CTAHeadingProps = React.ComponentProps<typeof HeadingContainer> & {
    //
  }
  
export type CTAButtonProps = & React.ComponentProps<typeof CTAButtonContainer> & {
    buttonURL?: string
  }

export type CTAProps =
| (
    {
      heading?: never
      buttons?: never
      content?: never
    } & CTAWrapperProps
  )
  | (
    {
      heading?: never
      buttons: true
      content?: never
    } & CTAButtonProps
  )
  | (
    {
      heading?: never
      buttons?: never
      content: true
    } & CTAContentProps
  )
  | (
    {
      heading: true
      buttons?: never
      content?: never
    } & CTAHeadingProps
  )

const StyledContainer = styled.div`
  position: relative;
  background-color: black;
  background-size: cover;
  width: cacl(100% + ${props => props.theme.spacing(2)});
  -webkit-backface-visibility: hidden;
  padding: ${irem(92)} 0 ${irem(72)};
  clip-path: polygon(0 0, 100% 25px, 100% calc(100% - 20px), 0 100%);
  margin: ${irem(50)} ${props => props.theme.spacing(-1)} ${irem(70)};

  &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background: rgba(0, 0, 0, 0.6);
    opacity: 1;
    z-index: 1;
  }

  ${props => props.theme.breakpoints.up('md')} {
    clip-path: polygon(0 0, 100% 10%, 100% 90%, 0 100%);
    min-height: 95vh;
    margin-bottom: ${irem(150)};

    &::after {
      content: none;
    }
  }
`

const HeadingContainer = styled(Typography)`
  & > span {
    display: block;
    box-shadow: unset;
    background-color: unset;
    font-size: ${props => props.theme.typography.pxToRem(28)};
    color: white;
    font-weight: 500;
    text-align: center;
    padding: unset;

    ${props => props.theme.breakpoints.up('md')} {
      width: unset;
      background-color: white;
      color: #1c1f2a;
      font-weight: 500;
      font-size: ${props => props.theme.typography.pxToRem(55)};
      padding: ${props => props.theme.typography.pxToRem(15)} ${props => props.theme.typography.pxToRem(50)} ${props => props.theme.typography.pxToRem(15)} ${props => props.theme.typography.pxToRem(50)};
      word-wrap: break-word;
      display: inline;
      margin-bottom: ${props => props.theme.typography.pxToRem(10)};
      box-decoration-break: clone;
      -webkit-box-decoration-break: clone;
      margin-left: ${props => props.theme.typography.pxToRem(-40)};
    }
  }
`

const ContentContainer = styled(Grid)`
  z-index: 2;
  max-width: ${props => props.theme.typography.pxToRem(300)};

  ${props => props.theme.breakpoints.up('md')} {
    max-width: ${props => props.theme.typography.pxToRem(650)};
  }

  ${props => props.theme.breakpoints.up('lg')} {
    left: 35%;
    max-width: ${props => props.theme.typography.pxToRem(650)};
  }
`

const CTAButtonContainer = styled(Grid)`
  margin-left: unset;
  margin-top: ${props => props.theme.typography.pxToRem(15)};

  ${props => props.theme.breakpoints.up('md')} {
    width: 100%;
    margin-top: ${props => props.theme.typography.pxToRem(50)};
    margin-left: ${props => props.theme.typography.pxToRem(-85)};
  }

  ${props => props.theme.breakpoints.up('lg')} {
    margin-left: ${props => props.theme.typography.pxToRem(-40)};
  }
`

const StyledContentContainer = styled(Container)`
  ${props => props.theme.breakpoints.up('md')} {
    height: 100vh;
  }
`

const StyledGrid = styled(Grid)`
  height: 100%;
`

export const CTA: React.FC<PropsWithChildren<CTAProps>> = ({ heading, buttons, content, children, ...props }) => {
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'))

  if (buttons) {
    return (
      <CTAButtonContainer
        item
        xs={12}
      >
        <Grid
          container
          direction="row"
          {...props}
        >
          {children}
        </Grid>
      </CTAButtonContainer>
    )
  }

  if (heading) {
    return (
      <CTAHeadingContainer
        { ...props }
      >
        {children}
      </CTAHeadingContainer>
    )
  }

  if (content) {
    return (
      <CTAContentContainer
        { ...props }
      >
        {children}
      </CTAContentContainer>
    )
  }

  return (
    <CTAWrapper
      { ...props as CTAWrapperProps }
    >      <StyledContentContainer maxWidth={'lg'} >
        <StyledGrid container alignItems="center" justifyContent={ isTablet ? 'center' :'flex-start'}>
          {children}
        </StyledGrid>
      </StyledContentContainer>
    </CTAWrapper>
  )
}

const CTAContentContainer: React.FC<PropsWithChildren<CTAContentProps>> = ({ children, ...props }) => {
  const tablet = useMediaQuery(theme.breakpoints.down('md'))

  return (
    <ContentContainer 
      container
      direction={tablet ? 'column' : 'row' }
      alignItems="center"
      justifyContent="flex-start"
      {...props}
    >
      {children}
    </ContentContainer>
  )
}

const CTAHeadingContainer: React.FC<PropsWithChildren<CTAHeadingProps>> = ({ children, ...props }) => (
  <HeadingContainer {...props}>
    <span>{children}</span>
  </HeadingContainer>
)

const CTAWrapper: React.FC<PropsWithChildren<CTAWrapperProps>> = ({ imageURL, imageAlt, children, ...props }) => (
  <StyledContainer {...props}
  >
    <Image 
      alt={imageAlt || ''}
      src={imageURL}
      layout="fill"
      objectFit="cover"
      quality={100}
    />
    {children}
  </StyledContainer>
)